import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileBarChart, ArrowLeft, FileSpreadsheet, FileText, Download, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { useObligaciones } from "@/hooks/useObligaciones";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const ReporteIntegrado = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { obligaciones, loading } = useObligaciones();
  const [seleccionados, setSeleccionados] = useState<string[]>([]);
  const [formatoExportar, setFormatoExportar] = useState<'excel' | 'pdf' | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggleSeleccion = (id: string) => {
    setSeleccionados(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const seleccionarTodos = () => {
    if (seleccionados.length === obligaciones.length) {
      setSeleccionados([]);
    } else {
      setSeleccionados(obligaciones.map(o => o.id));
    }
  };

  const exportarSeleccionados = async (formato: 'excel' | 'pdf') => {
    if (seleccionados.length === 0) {
      toast({
        title: 'Sin selección',
        description: 'Por favor selecciona al menos un programa para exportar',
        variant: 'destructive',
      });
      return;
    }

    try {
      toast({
        title: `Generando ${formato.toUpperCase()}`,
        description: 'Por favor espera, esto puede tomar unos momentos...',
      });

      if (formato === 'excel') {
        const { generarReporteCompletoExcel } = await import('@/components/reportes/GeneradorReporteCompleto');
        const obligacionesSeleccionadas = obligaciones.filter(o => seleccionados.includes(o.id));
        const workbook = await generarReporteCompletoExcel('2025', obligacionesSeleccionadas);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Reporte_Completo_ICA_${new Date().getFullYear()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        const { generarReporteCompletoPDF } = await import('@/components/reportes/GeneradorReportePDF');
        const obligacionesSeleccionadas = obligaciones.filter(o => seleccionados.includes(o.id));
        const pdf = await generarReporteCompletoPDF(obligacionesSeleccionadas, '2025');
        
        pdf.save(`Reporte_Completo_ICA_${new Date().getFullYear()}.pdf`);
      }

      toast({
        title: 'Reporte generado',
        description: `El reporte se ha descargado correctamente en formato ${formato.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Error al generar reporte:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el reporte',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/home")}
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <FileBarChart className="w-8 h-8 text-viable-orange" />
            <div>
              <h1 className="text-xl font-bold text-foreground">Reporte ICA</h1>
              <p className="text-sm text-muted-foreground">Reportes Consolidados del Sistema</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              onClick={() => navigate("/home")}
            >
              Volver al Inicio
            </Button>
            <Button variant="outline" size="sm" onClick={signOut}>
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 lg:pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Reporte General Integrado
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Selecciona los programas que deseas incluir en el reporte consolidado
              </p>
            </div>

            {/* Botones de Acceso Rápido */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-accent/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
                      <FileBarChart className="w-5 h-5 text-white" />
                    </div>
                    Reporte Social
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full bg-accent hover:bg-accent/90"
                    onClick={() => navigate("/reporte-social")}
                  >
                    Ver Reporte
                  </Button>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-viable-green/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-viable-green flex items-center justify-center">
                      <FileBarChart className="w-5 h-5 text-white" />
                    </div>
                    Reporte Ambiental
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full bg-viable-green hover:bg-viable-green-dark"
                    onClick={() => navigate("/reporte-ambiental")}
                  >
                    Ver Reporte
                  </Button>
                </CardContent>
              </Card>

              <Card className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-viable-blue/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-viable-blue flex items-center justify-center">
                      <FileBarChart className="w-5 h-5 text-white" />
                    </div>
                    Reporte SST
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full bg-viable-blue hover:bg-viable-blue-dark"
                    onClick={() => navigate("/reporte-sst")}
                  >
                    Ver Reporte
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Reporte Consolidado */}
            <Card>
              <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <CardHeader>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity">
                      <div className="text-left">
                        <CardTitle className="flex items-center gap-2">
                          Reporte Consolidado
                          <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </CardTitle>
                        <CardDescription>
                          Selecciona programas y genera reportes consolidados
                        </CardDescription>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                </CardHeader>
                
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Programas Disponibles */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">Programas Disponibles</h3>
                          <p className="text-sm text-muted-foreground">
                            {seleccionados.length} de {obligaciones.length} programas seleccionados
                          </p>
                        </div>
                        <Button 
                          variant="outline"
                          onClick={seleccionarTodos}
                        >
                          {seleccionados.length === obligaciones.length ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                        </Button>
                      </div>

                      {loading ? (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground">Cargando programas...</p>
                        </div>
                      ) : obligaciones.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-muted-foreground">No hay programas disponibles</p>
                        </div>
                      ) : (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12">
                                  <Checkbox
                                    checked={seleccionados.length === obligaciones.length}
                                    onCheckedChange={seleccionarTodos}
                                  />
                                </TableHead>
                                <TableHead>Código</TableHead>
                                <TableHead>Nombre del Programa</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Estado</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {obligaciones.map((obligacion) => (
                                <TableRow key={obligacion.id}>
                                  <TableCell>
                                    <Checkbox
                                      checked={seleccionados.includes(obligacion.id)}
                                      onCheckedChange={() => toggleSeleccion(obligacion.id)}
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">{obligacion.codigo}</TableCell>
                                  <TableCell className="font-medium">{obligacion.nombre_programa}</TableCell>
                                  <TableCell>
                                    <Badge variant={
                                      obligacion.tipo === 'Ambiental' ? 'default' :
                                      obligacion.tipo === 'SST' ? 'secondary' :
                                      'outline'
                                    }>
                                      {obligacion.tipo}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={
                                      obligacion.estado === 'Cumplido' ? 'default' :
                                      obligacion.estado === 'En Proceso' ? 'secondary' :
                                      obligacion.estado === 'Vencido' ? 'destructive' :
                                      'outline'
                                    }>
                                      {obligacion.estado}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}

                      {/* Botones de Exportación */}
                      {seleccionados.length > 0 && (
                        <div className="mt-6 flex gap-4 justify-center">
                          <Button
                            size="lg"
                            className="gap-2"
                            onClick={() => exportarSeleccionados('excel')}
                          >
                            <FileSpreadsheet className="w-5 h-5" />
                            Exportar a Excel
                          </Button>
                          <Button
                            size="lg"
                            variant="outline"
                            className="gap-2"
                            onClick={() => exportarSeleccionados('pdf')}
                          >
                            <FileText className="w-5 h-5" />
                            Exportar a PDF
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default ReporteIntegrado;
